import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import { Card, CardBody, Row, Col, Label, Input, Button, Alert, ListGroup, ListGroupItem, Badge } from "reactstrap";
import { PostData, GetRequest } from "../../../services/FetchData";
import { ProductContext } from "../../../store";
import { toast } from "react-toastify";
import Cookies from "js-cookie";
import DetailSkeleton from "../../../components/Skeleton/DetailSkeleton";

const NETSUITE_API = "api/v1/netsuite/";

const reactSelectStyles = {
	control: (base, state) => ({
		...base,
		minHeight: 38,
		fontSize: "0.875rem",
		borderColor: state.isFocused ? "#86b7fe" : "#dee2e6",
		boxShadow: state.isFocused ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)" : "none",
	}),
	menu: (base) => ({ ...base, zIndex: 20 }),
	menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const emptyColumnState = () => ({
	mode: "existing",
	existingSearchField: "",
	existingAiField: "",
	existingKey: "",
	newName: "",
	newTypesenseType: "string",
});

export default function NetSuiteCatalogSync() {
	const { currentWebsiteId, userLoader } = useContext(ProductContext);
	const websiteId = currentWebsiteId || Cookies.get("_website_id");

	const [pageLoader, setPageLoader] = useState(true);
	const [nsConnected, setNsConnected] = useState(false);

	const [catalogSyncEnabled, setCatalogSyncEnabled] = useState(false);
	const [syncToSearch, setSyncToSearch] = useState(false);
	const [syncToAi, setSyncToAi] = useState(false);
	const [hasSearchProduct, setHasSearchProduct] = useState(false);
	const [hasAiProduct, setHasAiProduct] = useState(false);

	const [productRecordType, setProductRecordType] = useState("inventoryItem");
	const [syncIntervalMinutes, setSyncIntervalMinutes] = useState("");
	const [lastSearchSyncAt, setLastSearchSyncAt] = useState(null);
	const [lastAiSyncAt, setLastAiSyncAt] = useState(null);
	const [lastSearchErr, setLastSearchErr] = useState(null);
	const [lastAiErr, setLastAiErr] = useState(null);

	const [uniqueNetsuitePath, setUniqueNetsuitePath] = useState("");
	const [uniqueCbSearchField, setUniqueCbSearchField] = useState("");
	const [uniqueCbAiField, setUniqueCbAiField] = useState("");
	const [selectedNetsuitePaths, setSelectedNetsuitePaths] = useState([]);
	const [columnByPath, setColumnByPath] = useState({});
	const [wizardStep, setWizardStep] = useState(1);

	const [metadataPaths, setMetadataPaths] = useState([]);
	const [pathsLoading, setPathsLoading] = useState(false);
	const [searchSchemaFields, setSearchSchemaFields] = useState([]);
	const [assistantSchemaFields, setAssistantSchemaFields] = useState([]);
	const [typesForNewFields, setTypesForNewFields] = useState(["string", "float", "int32", "bool", "string[]"]);
	const [typesenseStoreView, setTypesenseStoreView] = useState("");
	const [websitePlatformType, setWebsitePlatformType] = useState("");
	const [magentoStoreViews, setMagentoStoreViews] = useState([]);
	const [needsStoreViewSelection, setNeedsStoreViewSelection] = useState(false);
	const [productCollectionName, setProductCollectionName] = useState(null);

	const [assistantTips, setAssistantTips] = useState([]);
	const [assistantLoading, setAssistantLoading] = useState(false);

	const [saving, setSaving] = useState(false);
	const [syncing, setSyncing] = useState(false);
	const [catalogSyncInProgress, setCatalogSyncInProgress] = useState(false);
	const [syncRunJob, setSyncRunJob] = useState(null);

	const loadNsStatus = useCallback(async () => {
		if (!websiteId) return false;
		try {
			const res = await GetRequest(
				"ms1",
				`${NETSUITE_API}status?website_id=${encodeURIComponent(String(websiteId))}`
			);
			if (res?.status === "success" && res.data) {
				setNsConnected(!!res.data.connected);
				return !!res.data.connected;
			}
		} catch (e) {
			console.error(e);
		}
		return false;
	}, [websiteId]);

	const loadConversionBoxOptions = useCallback(async () => {
		if (!websiteId) return;
		try {
			const qs = new URLSearchParams({ website_id: String(websiteId) });
			const sv = String(typesenseStoreView || "").trim();
			if (sv) qs.set("store_view", sv);
			const res = await GetRequest("ms1", `${NETSUITE_API}conversionBoxOptions?${qs.toString()}`);
			if (res?.status === "success" && res.data) {
				setSearchSchemaFields(Array.isArray(res.data.searchSchemaFields) ? res.data.searchSchemaFields : []);
				setAssistantSchemaFields(Array.isArray(res.data.assistantSchemaFields) ? res.data.assistantSchemaFields : []);
				if (Array.isArray(res.data.typesForNewFields)) setTypesForNewFields(res.data.typesForNewFields);
				setWebsitePlatformType(res.data.websitePlatformType || "");
				setMagentoStoreViews(Array.isArray(res.data.magentoStoreViews) ? res.data.magentoStoreViews : []);
				setNeedsStoreViewSelection(!!res.data.needsStoreViewSelection);
				setProductCollectionName(res.data.productCollectionName ?? null);
			}
		} catch (e) {
			console.error(e);
		}
	}, [websiteId, typesenseStoreView]);

	const loadMetadataPaths = useCallback(async () => {
		if (!websiteId || !nsConnected) return;
		setPathsLoading(true);
		try {
			const res = await GetRequest(
				"ms1",
				`${NETSUITE_API}metadataPaths?website_id=${encodeURIComponent(String(websiteId))}&type=${encodeURIComponent(productRecordType)}`
			);
			if (res?.status === "success" && Array.isArray(res.data?.paths)) {
				setMetadataPaths(res.data.paths);
			} else {
				setMetadataPaths([]);
			}
		} catch (e) {
			console.error(e);
			setMetadataPaths([]);
		} finally {
			setPathsLoading(false);
		}
	}, [websiteId, nsConnected, productRecordType]);

	const loadSyncSettings = useCallback(async () => {
		if (!websiteId) return;
		try {
			const res = await GetRequest(
				"ms1",
				`${NETSUITE_API}syncSettings?website_id=${encodeURIComponent(String(websiteId))}`
			);
			if (res?.status === "success" && res.data) {
				const d = res.data;
				const sSearch = !!d.syncToSearch;
				const sAi = !!d.syncToAiAssistant;
				setSyncToSearch(sSearch);
				setSyncToAi(sAi);
				setCatalogSyncEnabled(sSearch || sAi);
				setHasSearchProduct(!!d.hasSearchProduct);
				setHasAiProduct(!!d.hasAiAssistantProduct);
				setProductRecordType(d.productRecordType || "inventoryItem");
				const iv = d.syncIntervalMinutes;
				setSyncIntervalMinutes(iv === null || iv === undefined ? "" : String(iv));
				setLastSearchSyncAt(d.lastSearchSyncAt || null);
				setLastAiSyncAt(d.lastAiSyncAt || null);
				setLastSearchErr(d.lastSearchSyncError || null);
				setLastAiErr(d.lastAiSyncError || null);
				setCatalogSyncInProgress(!!d.catalogSyncInProgress);
				setSyncRunJob(d.syncRunJob && typeof d.syncRunJob === "object" ? d.syncRunJob : null);

				const flow = d.netsuiteSyncFlow;
				if (flow && typeof flow === "object") {
					setTypesenseStoreView(flow.typesenseStoreView || "");
					setUniqueNetsuitePath(flow.uniqueNetsuitePath || "");
					setUniqueCbSearchField(flow.uniqueCbSearchField || "");
					setUniqueCbAiField(flow.uniqueCbAiField || "");
					setSelectedNetsuitePaths(Array.isArray(flow.selectedNetsuitePaths) ? flow.selectedNetsuitePaths : []);
					const cb = {};
					for (const row of flow.columnMappings || []) {
						if (!row?.nsPath) continue;
						cb[row.nsPath] = {
							mode: row.mode === "new" ? "new" : "existing",
							existingSearchField: row.existingSearchField || "",
							existingAiField: row.existingAiField || "",
							existingKey: row.existingKey || row.existingColumnId || "",
							newName: row.newName || row.newColumnName || "",
							newTypesenseType: row.newTypesenseType || row.newFieldType || "string",
						};
					}
					setColumnByPath(cb);
				}
			}
		} catch (e) {
			console.error(e);
		}
	}, [websiteId]);

	const refreshAssistantTips = async () => {
		if (!websiteId) return;
		setAssistantLoading(true);
		try {
			const res = await PostData("ms1", `${NETSUITE_API}syncMappingAssistant`, {
				website_id: Number(websiteId),
				step: String(wizardStep),
				context: {
					selectedNetsuitePaths,
					uniqueNetsuitePath,
					uniqueCbSearchField,
					uniqueCbAiField,
					productRecordType,
				},
			});
			if (res?.status === "success" && res.data?.tips) {
				setAssistantTips(res.data.tips);
			}
		} catch (e) {
			console.error(e);
		} finally {
			setAssistantLoading(false);
		}
	};

	useEffect(() => {
		if (!userLoader && websiteId) {
			setPageLoader(true);
			(async () => {
				await loadNsStatus();
				await loadSyncSettings();
				setPageLoader(false);
			})();
		}
	}, [userLoader, websiteId, loadNsStatus, loadSyncSettings]);

	useEffect(() => {
		if (!websiteId || userLoader || pageLoader) return;
		loadConversionBoxOptions();
	}, [websiteId, typesenseStoreView, userLoader, pageLoader, loadConversionBoxOptions]);

	useEffect(() => {
		if (nsConnected && websiteId) loadMetadataPaths();
	}, [nsConnected, websiteId, loadMetadataPaths]);

	useEffect(() => {
		let cancelled = false;
		async function loadTipsOnStep() {
			if (!websiteId) return;
			setAssistantLoading(true);
			try {
				const res = await PostData("ms1", `${NETSUITE_API}syncMappingAssistant`, {
					website_id: Number(websiteId),
					step: String(wizardStep),
					context: {},
				});
				if (!cancelled && res?.status === "success" && res.data?.tips) {
					setAssistantTips(res.data.tips);
				}
			} catch (e) {
				console.error(e);
			} finally {
				if (!cancelled) setAssistantLoading(false);
			}
		}
		loadTipsOnStep();
		return () => {
			cancelled = true;
		};
	}, [wizardStep, websiteId]);

	useEffect(() => {
		if (!websiteId || userLoader || pageLoader) return;
		const pollActive = catalogSyncInProgress || syncRunJob?.status === "running";
		if (!pollActive) return;
		const id = setInterval(() => {
			loadSyncSettings();
		}, 4000);
		return () => clearInterval(id);
	}, [
		websiteId,
		userLoader,
		pageLoader,
		catalogSyncInProgress,
		syncRunJob?.status,
		loadSyncSettings,
	]);

	useEffect(() => {
		setColumnByPath((prev) => {
			const next = { ...prev };
			for (const p of selectedNetsuitePaths) {
				if (!next[p]) next[p] = emptyColumnState();
			}
			for (const k of Object.keys(next)) {
				if (!selectedNetsuitePaths.includes(k)) delete next[k];
			}
			return next;
		});
	}, [selectedNetsuitePaths]);

	const netsuiteSelectOptions = useMemo(
		() => metadataPaths.map((p) => ({ value: p, label: p })),
		[metadataPaths]
	);

	const magentoStoreViewOptions = useMemo(
		() =>
			magentoStoreViews.map((v) => ({
				value: v.code,
				label: `${v.name || v.code} (${v.code})`,
			})),
		[magentoStoreViews]
	);

	const magentoStoreViewBlocking =
		String(websitePlatformType).toLowerCase() === "magento" &&
		catalogSyncEnabled &&
		syncToSearch &&
		hasSearchProduct &&
		(needsStoreViewSelection || magentoStoreViews.length > 1) &&
		!String(typesenseStoreView).trim();

	const step1Valid = useMemo(() => {
		if (!uniqueNetsuitePath.trim()) return false;
		if (magentoStoreViewBlocking) return false;
		if (catalogSyncEnabled && syncToSearch && hasSearchProduct && !String(uniqueCbSearchField).trim()) return false;
		if (catalogSyncEnabled && syncToAi && hasAiProduct && !String(uniqueCbAiField).trim()) return false;
		return true;
	}, [
		uniqueNetsuitePath,
		magentoStoreViewBlocking,
		catalogSyncEnabled,
		syncToSearch,
		syncToAi,
		hasSearchProduct,
		hasAiProduct,
		uniqueCbSearchField,
		uniqueCbAiField,
	]);
	const step2Valid = selectedNetsuitePaths.length > 0;
	const step3Valid = useMemo(() => {
		if (!step2Valid) return false;
		for (const p of selectedNetsuitePaths) {
			const row = columnByPath[p] || emptyColumnState();
			if (row.mode === "new") {
				if (!(row.newName || "").trim()) return false;
			} else {
				if (catalogSyncEnabled && syncToSearch && hasSearchProduct && !(row.existingSearchField || "").trim()) {
					return false;
				}
				if (catalogSyncEnabled && syncToAi && hasAiProduct && !(row.existingAiField || "").trim()) {
					return false;
				}
			}
		}
		return true;
	}, [
		selectedNetsuitePaths,
		columnByPath,
		step2Valid,
		catalogSyncEnabled,
		syncToSearch,
		syncToAi,
		hasSearchProduct,
		hasAiProduct,
	]);

	const buildSyncFlowPayload = () => ({
		typesenseStoreView: String(typesenseStoreView || "").trim(),
		uniqueNetsuitePath: uniqueNetsuitePath.trim(),
		uniqueCbSearchField: String(uniqueCbSearchField || "").trim(),
		uniqueCbAiField: String(uniqueCbAiField || "").trim(),
		selectedNetsuitePaths,
		columnMappings: selectedNetsuitePaths.map((nsPath) => {
			const row = columnByPath[nsPath] || emptyColumnState();
			if (row.mode === "new") {
				return {
					nsPath,
					mode: "new",
					newName: (row.newName || "").trim(),
					newTypesenseType: row.newTypesenseType || "string",
				};
			}
			return {
				nsPath,
				mode: "existing",
				existingSearchField: String(row.existingSearchField || "").trim(),
				existingAiField: String(row.existingAiField || "").trim(),
			};
		}),
	});

	const handleSave = async (e) => {
		e.preventDefault();
		if (!websiteId || !nsConnected) return;
		if (catalogSyncEnabled && (!step1Valid || !step2Valid || !step3Valid)) {
			toast.error("Complete all wizard steps before saving.");
			return;
		}
		setSaving(true);
		try {
			let sync_interval_minutes = null;
			if (syncIntervalMinutes !== "" && syncIntervalMinutes !== "manual") {
				const n = Number(syncIntervalMinutes);
				sync_interval_minutes = Number.isFinite(n) && n !== 0 ? n : null;
			}
			const enableSearch = catalogSyncEnabled && hasSearchProduct ? syncToSearch : false;
			const enableAi = catalogSyncEnabled && hasAiProduct ? syncToAi : false;

			const body = {
				website_id: Number(websiteId),
				sync_to_search: enableSearch,
				sync_to_ai_assistant: enableAi,
				product_record_type: productRecordType,
				sync_interval_minutes,
			};
			if (catalogSyncEnabled) {
				body.sync_flow = buildSyncFlowPayload();
			}

			const res = await PostData("ms1", `${NETSUITE_API}syncSettings`, body);
			if (res?.status === "success") {
				toast.success(res.message || "Saved.");
				await loadSyncSettings();
			} else {
				toast.error(res?.message || "Save failed.");
			}
		} catch (err) {
			console.error(err);
			toast.error("Save failed.");
		} finally {
			setSaving(false);
		}
	};

	const handleSyncNow = async () => {
		if (!websiteId || !nsConnected || catalogSyncInProgress) return;
		const scope =
			syncToSearch && syncToAi ? "both" : syncToSearch ? "search" : syncToAi ? "aiassistant" : "both";
		setSyncing(true);
		try {
			const res = await PostData("ms1", `${NETSUITE_API}syncRun`, {
				website_id: Number(websiteId),
				scope,
			});
			if (res?.status === "success") {
				toast.success(res.message || "Sync started.");
				await loadSyncSettings();
			} else {
				toast.error(res?.message || "Could not start sync.");
			}
		} catch (err) {
			console.error(err);
			toast.error("Could not start sync.");
		} finally {
			setSyncing(false);
		}
	};

	const applySuggestedMappings = async () => {
		try {
			const searchFieldNames = searchSchemaFields.map((o) => o.value);
			const aiFieldNames = assistantSchemaFields.map((o) => o.value);
			const res = await PostData("ms1", `${NETSUITE_API}syncMappingAssistant`, {
				website_id: Number(websiteId),
				step: "3",
				context: { selectedNetsuitePaths, searchFieldNames, aiFieldNames },
			});
			const rows = res?.data?.suggestedRows;
			if (!Array.isArray(rows) || rows.length === 0) {
				toast.info("No automatic suggestions for the current selection.");
				return;
			}
			setColumnByPath((prev) => {
				const next = { ...prev };
				for (const s of rows) {
					if (!s.nsPath) continue;
					next[s.nsPath] = {
						mode: "existing",
						existingSearchField: s.suggestedExistingSearchField || "",
						existingAiField: s.suggestedExistingAiField || "",
						existingKey: "",
						newName: "",
						newTypesenseType: "string",
					};
				}
				return next;
			});
			toast.success("Applied suggested column matches where possible.");
		} catch (e) {
			console.error(e);
			toast.error("Could not load suggestions.");
		}
	};

	const updateColumn = (nsPath, patch) => {
		setColumnByPath((prev) => ({
			...prev,
			[nsPath]: { ...(prev[nsPath] || emptyColumnState()), ...patch },
		}));
	};

	if (userLoader || pageLoader) {
		return <DetailSkeleton />;
	}

	if (!websiteId) {
		return (
			<div className="pageContent">
				<Alert color="warning">Select a website first.</Alert>
			</div>
		);
	}

	const selectMenuPortal = typeof document !== "undefined" ? document.body : null;

	return (
		<div className="pageContent integrations-page">
			<div className="pageHeader d-flex flex-column flex-md-row justify-content-between align-items-start gap-3">
				<div>
					<h2 className="pageTitle">NetSuite catalog sync</h2>
					<p className="text-muted mb-0">
						Connect NetSuite items to ConversionBox. Configure matching keys, choose which fields to synchronize, and
						map them to your catalog. Sync runs on a schedule or when you run it manually.
					</p>
				</div>
				<Button tag={Link} to="/user/integrations" color="outline-secondary" className="text-nowrap">
					← Back to Integrations
				</Button>
			</div>

			{!nsConnected ? (
				<Alert color="warning" className="mt-4">
					<strong>NetSuite is not connected.</strong> Connect OAuth in{" "}
					<Link to="/user/integrations" className="alert-link">
						Integrations
					</Link>{" "}
					first.
				</Alert>
			) : (
				<form onSubmit={handleSave}>
					<Row className="g-4 mt-2">
						<Col lg={8}>
							<Card className="border-0 shadow-sm mb-4">
								<CardBody>
									<h5 className="mb-3">Synchronization</h5>
									<div className="form-check mb-3">
										<Input
											id="catalog-sync-master"
											type="checkbox"
											className="form-check-input"
											checked={catalogSyncEnabled}
											disabled={saving}
											onChange={(e) => {
												const v = e.target.checked;
												setCatalogSyncEnabled(v);
												if (!v) {
													setSyncToSearch(false);
													setSyncToAi(false);
												}
											}}
										/>
										<Label check for="catalog-sync-master" className="fw-semibold">
											Enable catalog synchronization
										</Label>
										<p className="text-muted small mb-0 mt-1">
											When enabled, NetSuite catalog data is pushed into ConversionBox on your schedule or when you run
											sync now.
										</p>
									</div>

									{catalogSyncEnabled && (
										<div className="border rounded p-3 bg-light mb-0">
											<p className="small text-muted mb-2">Choose which catalogs receive updates for this website:</p>
											<div className="form-check mb-2">
												<Input
													id="idx-discovery"
													type="checkbox"
													className="form-check-input"
													checked={syncToSearch}
													disabled={!hasSearchProduct || saving}
													onChange={(e) => setSyncToSearch(e.target.checked)}
												/>
												<Label check for="idx-discovery" className="small">
													Product discovery
													{!hasSearchProduct && (
														<span className="text-muted ms-1">— not enabled for this site</span>
													)}
												</Label>
											</div>
											<div className="form-check mb-0">
												<Input
													id="idx-assistant"
													type="checkbox"
													className="form-check-input"
													checked={syncToAi}
													disabled={!hasAiProduct || saving}
													onChange={(e) => setSyncToAi(e.target.checked)}
												/>
												<Label check for="idx-assistant" className="small">
													Shopping assistant
													{!hasAiProduct && (
														<span className="text-muted ms-1">— not enabled for this site</span>
													)}
												</Label>
											</div>
											{hasSearchProduct &&
												String(websitePlatformType).toLowerCase() === "magento" &&
												magentoStoreViews.length > 0 && (
													<div className="mt-3 pt-3 border-top border-secondary border-opacity-25">
														<Label className="small fw-semibold d-block">Product discovery — store view</Label>
														<p className="text-muted small mb-2">
															Each store view has its own product index (same pattern as your on-site search). Choose
															the store whose product data NetSuite should update.
														</p>
														{productCollectionName && (
															<p className="small font-monospace text-secondary mb-2 mb-md-3">
																Current product index: {productCollectionName}
															</p>
														)}
														{needsStoreViewSelection && !String(typesenseStoreView).trim() && (
															<Alert color="warning" className="py-2 small mb-2">
																Select a store view to load product discovery field schema.
															</Alert>
														)}
														<Select
															inputId="magento-store-view"
															classNamePrefix="react-select"
															styles={reactSelectStyles}
															options={magentoStoreViewOptions}
															value={
																magentoStoreViewOptions.find((o) => o.value === typesenseStoreView) ||
																null
															}
															onChange={(opt) => setTypesenseStoreView(opt?.value || "")}
															placeholder="Search or select store view…"
															isDisabled={saving}
															isClearable={magentoStoreViews.length > 1}
															isSearchable
															menuPortalTarget={selectMenuPortal}
															menuPosition="fixed"
														/>
													</div>
												)}
										</div>
									)}
								</CardBody>
							</Card>

							{catalogSyncEnabled && (
								<>
									<div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
										{[1, 2, 3].map((s) => (
											<Button
												key={s}
												type="button"
												color={wizardStep === s ? "primary" : "outline-secondary"}
												size="sm"
												onClick={() => setWizardStep(s)}
												disabled={
													(s >= 2 && !step1Valid) ||
													(s >= 3 && !step2Valid)
												}
											>
												Step {s}
												{s === 1 && " — Unique keys"}
												{s === 2 && " — NetSuite fields"}
												{s === 3 && " — Column mapping"}
											</Button>
										))}
									</div>

									{wizardStep === 1 && (
										<Card className="border-0 shadow-sm mb-4">
											<CardBody>
												<h6 className="mb-3">Unique identifiers</h6>
												<p className="text-muted small">
													Choose one NetSuite field that uniquely identifies items, then pick the matching attribute
													from your website catalog schema for each enabled destination below.
												</p>
												<Row className="g-3">
													<Col xs={12}>
														<Label className="small fw-semibold">NetSuite identifier field</Label>
														<Select
															inputId="unique-ns-field"
															classNamePrefix="react-select"
															styles={reactSelectStyles}
															options={netsuiteSelectOptions}
															value={
																netsuiteSelectOptions.find((o) => o.value === uniqueNetsuitePath) || null
															}
															onChange={(opt) => setUniqueNetsuitePath(opt?.value || "")}
															placeholder={pathsLoading ? "Loading fields…" : "Search or select…"}
															isDisabled={saving || pathsLoading}
															isClearable
															isSearchable
															menuPortalTarget={selectMenuPortal}
															menuPosition="fixed"
														/>
													</Col>
													{catalogSyncEnabled && syncToSearch && hasSearchProduct && (
														<Col xs={12} md={6}>
															<Label className="small fw-semibold">
																ConversionBox identifier — product discovery (website search schema)
															</Label>
															<Select
																inputId="unique-cb-search"
																classNamePrefix="react-select"
																styles={reactSelectStyles}
																options={searchSchemaFields}
																value={
																	searchSchemaFields.find((o) => o.value === uniqueCbSearchField) ||
																	null
																}
																onChange={(opt) => setUniqueCbSearchField(opt?.value || "")}
																placeholder={
																	searchSchemaFields.length ? "Search or select…" : "Schema loading or empty"
																}
																isDisabled={saving}
																isClearable
																isSearchable
																menuPortalTarget={selectMenuPortal}
																menuPosition="fixed"
															/>
														</Col>
													)}
													{catalogSyncEnabled && syncToAi && hasAiProduct && (
														<Col xs={12} md={6}>
															<Label className="small fw-semibold">
																ConversionBox identifier — shopping assistant (assistant product schema)
															</Label>
															<Select
																inputId="unique-cb-ai"
																classNamePrefix="react-select"
																styles={reactSelectStyles}
																options={assistantSchemaFields}
																value={
																	assistantSchemaFields.find((o) => o.value === uniqueCbAiField) ||
																	null
																}
																onChange={(opt) => setUniqueCbAiField(opt?.value || "")}
																placeholder={
																	assistantSchemaFields.length
																		? "Search or select…"
																		: "Schema loading or empty"
																}
																isDisabled={saving}
																isClearable
																isSearchable
																menuPortalTarget={selectMenuPortal}
																menuPosition="fixed"
															/>
														</Col>
													)}
												</Row>
												<div className="mt-3 d-flex gap-2">
													<Button
														type="button"
														color="primary"
														size="sm"
														disabled={!step1Valid}
														onClick={() => setWizardStep(2)}
													>
														Continue
													</Button>
												</div>
											</CardBody>
										</Card>
									)}

									{wizardStep === 2 && (
										<Card className="border-0 shadow-sm mb-4">
											<CardBody>
												<h6 className="mb-3">Fields to synchronize</h6>
												<p className="text-muted small">
													Select NetSuite attributes to keep updated (you map each one in the next step).
												</p>
												<Label className="small fw-semibold">NetSuite fields</Label>
												<Select
													inputId="ns-fields-multi"
													classNamePrefix="react-select"
													styles={reactSelectStyles}
													isMulti
													options={netsuiteSelectOptions}
													value={netsuiteSelectOptions.filter((o) =>
														selectedNetsuitePaths.includes(o.value)
													)}
													onChange={(opts) =>
														setSelectedNetsuitePaths((opts || []).map((o) => o.value))
													}
													placeholder={
														pathsLoading ? "Loading fields…" : "Search or select one or more fields…"
													}
													isDisabled={saving || pathsLoading}
													closeMenuOnSelect={false}
													menuPortalTarget={selectMenuPortal}
													menuPosition="fixed"
												/>
												<div className="mt-3 d-flex gap-2 flex-wrap">
													<Button type="button" color="outline-secondary" size="sm" onClick={() => setWizardStep(1)}>
														Back
													</Button>
													<Button
														type="button"
														color="primary"
														size="sm"
														disabled={!step2Valid}
														onClick={() => setWizardStep(3)}
													>
														Continue
													</Button>
												</div>
											</CardBody>
										</Card>
									)}

									{wizardStep === 3 && (
										<Card className="border-0 shadow-sm mb-4">
											<CardBody>
												<h6 className="mb-3">Map to ConversionBox columns</h6>
												<p className="text-muted small">
													For each NetSuite field, map to an existing catalog column or create a new attribute. New
													attributes update your product discovery index schema when that catalog is enabled.
												</p>
												<div className="mb-3">
													<Button type="button" color="outline-primary" size="sm" onClick={applySuggestedMappings}>
														Apply suggested matches
													</Button>
												</div>
												{selectedNetsuitePaths.map((nsPath) => {
													const row = columnByPath[nsPath] || emptyColumnState();
													return (
														<div key={nsPath} className="border rounded p-3 mb-3 bg-white">
															<div className="small font-monospace fw-semibold mb-2">{nsPath}</div>
															<Row className="g-2 align-items-end">
																<Col xs={12} md={4}>
																	<Label className="small mb-1">Mapping</Label>
																	<Input
																		type="select"
																		value={row.mode}
																		disabled={saving}
																		onChange={(e) =>
																			updateColumn(nsPath, {
																				mode: e.target.value,
																			})
																		}
																	>
																		<option value="existing">Existing column</option>
																		<option value="new">New attribute</option>
																	</Input>
																</Col>
																{row.mode === "existing" ? (
																	<>
																		{catalogSyncEnabled && syncToSearch && hasSearchProduct && (
																			<Col
																				xs={12}
																				md={
																					catalogSyncEnabled &&
																					syncToAi &&
																					hasAiProduct
																						? 6
																						: 12
																				}
																			>
																				<Label className="small mb-1">
																					Product discovery column (search schema)
																				</Label>
																				<Select
																					classNamePrefix="react-select"
																					styles={reactSelectStyles}
																					options={searchSchemaFields}
																					value={
																						searchSchemaFields.find(
																							(o) => o.value === row.existingSearchField
																						) || null
																					}
																					onChange={(opt) =>
																						updateColumn(nsPath, {
																							existingSearchField: opt?.value || "",
																						})
																					}
																					placeholder="Select column…"
																					isDisabled={saving}
																					isClearable
																					isSearchable
																					menuPortalTarget={selectMenuPortal}
																					menuPosition="fixed"
																				/>
																			</Col>
																		)}
																		{catalogSyncEnabled && syncToAi && hasAiProduct && (
																			<Col
																				xs={12}
																				md={
																					catalogSyncEnabled &&
																					syncToSearch &&
																					hasSearchProduct
																						? 6
																						: 12
																				}
																			>
																				<Label className="small mb-1">
																					Shopping assistant column (assistant schema)
																				</Label>
																				<Select
																					classNamePrefix="react-select"
																					styles={reactSelectStyles}
																					options={assistantSchemaFields}
																					value={
																						assistantSchemaFields.find(
																							(o) => o.value === row.existingAiField
																						) || null
																					}
																					onChange={(opt) =>
																						updateColumn(nsPath, {
																							existingAiField: opt?.value || "",
																						})
																					}
																					placeholder="Select column…"
																					isDisabled={saving}
																					isClearable
																					isSearchable
																					menuPortalTarget={selectMenuPortal}
																					menuPosition="fixed"
																				/>
																			</Col>
																		)}
																	</>
																) : (
																	<>
																		<Col xs={12} md={4}>
																			<Label className="small mb-1">New attribute name</Label>
																			<Input
																				value={row.newName}
																				disabled={saving}
																				className="font-monospace"
																				onChange={(e) =>
																					updateColumn(nsPath, {
																						newName: e.target.value.replace(/[^a-zA-Z0-9_]/g, "_"),
																					})
																				}
																				placeholder="e.g. ns_custom_field"
																			/>
																		</Col>
																		<Col xs={12} md={4}>
																			<Label className="small mb-1">Storage type</Label>
																			<Input
																				type="select"
																				value={row.newTypesenseType || "string"}
																				disabled={saving}
																				onChange={(e) =>
																					updateColumn(nsPath, {
																						newTypesenseType: e.target.value,
																					})
																				}
																			>
																				{typesForNewFields.map((t) => (
																					<option key={t} value={t}>
																						{t}
																					</option>
																				))}
																			</Input>
																		</Col>
																	</>
																)}
															</Row>
														</div>
													);
												})}
												<div className="d-flex gap-2 flex-wrap mt-2">
													<Button type="button" color="outline-secondary" size="sm" onClick={() => setWizardStep(2)}>
														Back
													</Button>
												</div>
											</CardBody>
										</Card>
									)}
								</>
							)}

							<Card className="border-0 shadow-sm mb-4">
								<CardBody>
									<h6 className="mb-3">Schedule &amp; record type</h6>
									<Row className="g-3">
										<Col md={6}>
											<Label className="small fw-semibold">NetSuite record type</Label>
											<Input
												type="select"
												value={productRecordType}
												disabled={saving}
												onChange={(e) => setProductRecordType(e.target.value)}
											>
												<option value="inventoryItem">inventoryItem</option>
												<option value="assemblyItem">assemblyItem</option>
												<option value="nonInventorySaleItem">nonInventorySaleItem</option>
												<option value="serviceSaleItem">serviceSaleItem</option>
												<option value="downloadItem">downloadItem</option>
												<option value="giftCertificateItem">giftCertificateItem</option>
											</Input>
										</Col>
										<Col md={6}>
											<Label className="small fw-semibold">Frequency</Label>
											<Input
												type="select"
												value={syncIntervalMinutes === "" ? "" : syncIntervalMinutes}
												disabled={saving}
												onChange={(e) => setSyncIntervalMinutes(e.target.value)}
											>
												<option value="">Manual only</option>
												<option value="15">Every 15 minutes</option>
												<option value="60">Hourly</option>
												<option value="360">Every 6 hours</option>
												<option value="1440">Daily</option>
											</Input>
										</Col>
									</Row>
									{catalogSyncInProgress && (
										<Alert color="info" className="mt-3 mb-0 py-2">
											Catalog sync is running
											{typeof syncRunJob?.processedSoFar === "number"
												? ` — ${syncRunJob.processedSoFar} product(s) processed so far`
												: ""}
											.
										</Alert>
									)}
									{syncRunJob?.status === "failed" && syncRunJob?.error && (
										<Alert color="danger" className="mt-3 mb-0 py-2">
											Sync failed: {syncRunJob.error}
										</Alert>
									)}
									<div className="d-flex flex-wrap gap-2 mt-3">
										<Button color="primary" type="submit" disabled={saving}>
											{saving ? "Saving…" : "Save configuration"}
										</Button>
										<Button
											color="outline-primary"
											type="button"
											disabled={
												syncing ||
												saving ||
												!catalogSyncEnabled ||
												catalogSyncInProgress ||
												syncRunJob?.status === "running"
											}
											title={
												catalogSyncInProgress || syncRunJob?.status === "running"
													? "A catalog sync is already running or queued."
													: undefined
											}
											onClick={handleSyncNow}
										>
											{syncing ? "Starting…" : "Sync now"}
										</Button>
									</div>
									{(lastSearchSyncAt || lastAiSyncAt || lastSearchErr || lastAiErr) && (
										<div className="small text-muted border rounded p-2 mt-3 bg-white">
											{lastSearchSyncAt && (
												<div>Last product discovery sync: {new Date(lastSearchSyncAt).toLocaleString()}</div>
											)}
											{lastAiSyncAt && (
												<div>Last shopping assistant sync: {new Date(lastAiSyncAt).toLocaleString()}</div>
											)}
											{lastSearchErr && (
												<div className="text-danger mt-1">Discovery sync error: {lastSearchErr}</div>
											)}
											{lastAiErr && (
												<div className="text-danger mt-1">Assistant sync error: {lastAiErr}</div>
											)}
										</div>
									)}
								</CardBody>
							</Card>
						</Col>

						<Col lg={4}>
							<Card className="border-0 shadow-sm sticky-top" style={{ top: "1rem" }}>
								<CardBody>
									<div className="d-flex justify-content-between align-items-center mb-2">
										<h6 className="mb-0">Mapping assistant</h6>
										<Badge color="light" className="text-muted border">
											Step {wizardStep}
										</Badge>
									</div>
									<p className="text-muted small">
										Contextual guidance for identifiers, field selection, and column mapping. Refreshes when you change
										steps.
									</p>
									<Button
										type="button"
										color="outline-secondary"
										size="sm"
										className="mb-3"
										disabled={assistantLoading}
										onClick={refreshAssistantTips}
									>
										{assistantLoading ? "Refreshing…" : "Refresh tips"}
									</Button>
									<ListGroup flush className="border rounded">
										{(assistantTips.length ? assistantTips : ["Tips will load for the current step."]).map(
											(tip, i) => (
												<ListGroupItem key={i} className="small text-muted border-0 py-2">
													{tip}
												</ListGroupItem>
											)
										)}
									</ListGroup>
								</CardBody>
							</Card>
						</Col>
					</Row>
				</form>
			)}
		</div>
	);
}
